import { useCallback, useEffect, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import uuid from "uuid";

import type { APIError } from "simplydo/core/api";
import { SubscribedWebsocket } from "simplydo/core/api/websocket";
import {
  type IdeaUpdatePayload,
  diffIdeaUpdatePayload,
  mergeIdeaUpdatePayload,
  mergePendingIdeaPayloads,
  reducePath,
} from "simplydo/core/ideaUpdates";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";

import api, { websocketApi } from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import type {
  IdeaCollaboratorUser,
  IdeaDetail,
  IdeaEditorState,
  IdeaExternalChanges,
  UpdateIdeaFn,
  UseIdeaEditorReturn,
} from "../types";
import { useIdeaIdState } from "./useIdea";

export { reducePath };

const maxHistoryLength = 500;

/**
 * Presence indicators are shown per idea field (FieldHeader), so nested
 * edit paths like ["templated", fieldId, "value"] must collapse to the
 * field itself: ["templated", fieldId].
 */
const presencePathFor = (fieldId: string[]): string[] => {
  if (!fieldId.length) return [];
  if (fieldId[0] === "templated" || fieldId[0] === "assessment") {
    return fieldId.slice(0, Math.min(2, fieldId.length));
  }
  return fieldId.slice(0, 1);
};

const resolveEditPath = (
  fieldId: string[] | string | undefined,
  templated?: boolean,
): string[] => {
  if (Array.isArray(fieldId)) return fieldId;
  if (typeof fieldId === "string" && fieldId) {
    return templated === false ? [fieldId] : ["templated", fieldId];
  }
  return [];
};

const isCollaboratorUser = (value: unknown): value is IdeaCollaboratorUser =>
  !!value &&
  typeof value === "object" &&
  typeof (value as IdeaCollaboratorUser)._id === "string";

/** Coerce legacy array / nested-path presence data into a userId → user map. */
const normalizeEditors = (
  existing: unknown,
): Record<string, IdeaCollaboratorUser> => {
  if (!existing) return {};
  if (Array.isArray(existing)) {
    return Object.fromEntries(
      existing.filter(isCollaboratorUser).map((user) => [user._id, user]),
    );
  }
  if (typeof existing !== "object") return {};

  const result: Record<string, IdeaCollaboratorUser> = {};
  for (const value of Object.values(existing as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      for (const user of value) {
        if (isCollaboratorUser(user)) result[user._id] = user;
      }
    } else if (isCollaboratorUser(value)) {
      result[value._id] = value;
    }
  }
  return result;
};

const removeUserFromExternalChanges = (
  changes: IdeaExternalChanges | undefined,
  userId: string,
): IdeaExternalChanges => {
  if (!changes) return {};

  const cleanNode = (node: unknown): unknown => {
    if (!node || typeof node !== "object") return node;
    if (Array.isArray(node)) {
      return node.filter(
        (entry) => !isCollaboratorUser(entry) || entry._id !== userId,
      );
    }

    const cleaned: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(
      node as Record<string, unknown>,
    )) {
      if (
        key === userId ||
        (isCollaboratorUser(value) && value._id === userId)
      ) {
        continue;
      }
      const nextValue = cleanNode(value);
      if (Array.isArray(nextValue) && nextValue.length === 0) continue;
      if (
        nextValue &&
        typeof nextValue === "object" &&
        !Array.isArray(nextValue) &&
        Object.keys(nextValue as object).length === 0
      ) {
        continue;
      }
      cleaned[key] = nextValue;
    }
    return cleaned;
  };

  return cleanNode(changes) as IdeaExternalChanges;
};

type IdeaWebsocketEvent = {
  channel: string;
  fieldId: string[] | string;
  fieldData: unknown;
  templated?: boolean;
  toUser: string;
  user: IdeaCollaboratorUser;
};

type IdeaHistorySnapshot = {
  templated?: OpenAPI.Schemas["Idea"]["templated"];
  name?: OpenAPI.Schemas["Idea"]["name"];
  coverImage?: OpenAPI.Schemas["Idea"]["coverImage"];
};

export const useIdeaEditor = (): UseIdeaEditorReturn => {
  const [ideaId] = useIdeaIdState();

  const [historyState, setHistoryState] = useState<{
    undo: IdeaHistorySnapshot[];
    redo: IdeaHistorySnapshot[];
  }>({ undo: [], redo: [] });
  const [websocketState, setWebsocketState] = useState<{
    otherUsers: Record<string, IdeaCollaboratorUser>;
    websocketConnected: boolean;
    externalChanges?: IdeaExternalChanges;
  }>({ otherUsers: {}, websocketConnected: true });
  const [unsaved, setUnsaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | "">("");
  const editTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ideaAtStartOfUpdate = useRef<OpenAPI.Schemas["Idea"] | null>(null);
  const pendingChangesRef = useRef<IdeaUpdatePayload>({});
  const flushPendingChangesRef = useRef<() => void>(() => {});
  const websocketSubscription = useRef<SubscribedWebsocket | null>(null);
  const websocketSubscriptionHandler = useRef<
    ((data: IdeaWebsocketEvent) => void) | null
  >(null);
  const currentSave = useRef("");
  const updatedAt = useRef(new Date());
  const user = useAppSelector((state) => state.user);
  const userId = user?._id;
  const { t } = useTranslation();

  const ideaQuery = useQuery(
    initUseQuery(api.ideas.get, [ideaId!], {
      isEnabled: !!ideaId,
      placeholderData: keepPreviousData,
    }),
  );
  const idea = ideaQuery.data;

  const ideaState: IdeaEditorState = {
    unsaved,
    isSaving,
    savedAt,
    notFound: ideaQuery.isError,
    errorMessage: (ideaQuery.error as APIError | null)?.message ?? "",
  };

  const isCollaborator = !!idea?.authors?.some(
    (author) => author._id === userId,
  );
  // Avoid subscribing before we know collaborator status. A quiet→loud upgrade
  // only updates options on the server and skips the userSubscribed announce.
  const ideaReady = !!idea && idea._id === ideaId;

  const reloadIdea = useCallback(() => {
    api.invalidateQueries(api.ideas.get, [ideaId]);
  }, [ideaId]);

  useEffect(() => {
    setHistoryState({ undo: [], redo: [] });
    setUnsaved(false);
    setSavedAt("");
    setWebsocketState({ otherUsers: {}, websocketConnected: true });
    ideaAtStartOfUpdate.current = null;
    pendingChangesRef.current = {};
    if (editTimeout.current !== null) {
      clearTimeout(editTimeout.current);
      editTimeout.current = null;
    }
  }, [ideaId]);

  const flushPendingChanges = useCallback(() => {
    const toSend = pendingChangesRef.current;
    if (!Object.keys(toSend).length) {
      return;
    }
    pendingChangesRef.current = {};

    const fetchId = uuid.v4();
    currentSave.current = fetchId;
    const savedAtTime = new Date();

    setIsSaving(true);
    api.ideas.update(
      ideaId,
      toSend,
      (serverResponse: OpenAPI.PUT<"/ideas/{id}">["response"]) => {
        const serverData = (serverResponse ?? {}) as Partial<IdeaDetail>;
        const templated = serverData.templated;
        const serverChanges = { ...serverData };
        delete serverChanges.templated;
        delete serverChanges.name;
        delete serverChanges.ownerIdeaTemplate;
        if (currentSave.current !== fetchId) {
          return;
        }

        api.setQueryData(api.ideas.get, [ideaId], (prev) => {
          if (!prev) {
            return prev;
          }
          return Object.assign({}, prev, serverChanges, {
            templated:
              updatedAt.current > savedAtTime ? prev.templated : templated,
          }) as IdeaDetail;
        });
        setIsSaving(false);
        if (Object.keys(pendingChangesRef.current).length > 0) {
          setUnsaved(true);
          flushPendingChangesRef.current();
        } else {
          setUnsaved(false);
          setSavedAt(savedAtTime);
        }
      },
      (err) => {
        pendingChangesRef.current = mergePendingIdeaPayloads(
          toSend,
          pendingChangesRef.current,
        );
        if (err.errorCode === "ideaIsSubmitted") {
          toast.error(
            `This ${t("generic.idea")} cannot be edited because it has been submitted.`,
          );

          setIsSaving(false);
          reloadIdea();
        } else {
          toast.error(err.message);
          setIsSaving(false);
          setUnsaved(true);
        }
      },
    );
  }, [ideaId, t, reloadIdea]);

  flushPendingChangesRef.current = flushPendingChanges;

  const saveIdeaPartial = useCallback(
    (changes: IdeaUpdatePayload) => {
      pendingChangesRef.current = mergePendingIdeaPayloads(
        pendingChangesRef.current,
        changes,
      );
      updatedAt.current = new Date();
      setUnsaved(true);
      flushPendingChanges();
    },
    [flushPendingChanges],
  );

  const asHistoryState = useCallback(
    (
      historyIdea: Pick<
        OpenAPI.Schemas["Idea"],
        "templated" | "name" | "coverImage"
      >,
    ) => ({
      templated: historyIdea.templated,
      name: historyIdea.name,
      coverImage: historyIdea.coverImage,
    }),
    [],
  );

  const addHistoryEntry = useCallback(
    (updatedIdea: Partial<OpenAPI.Schemas["Idea"]>) => {
      setHistoryState((prevHistory) => {
        const slicedUndoHistory = prevHistory.undo.slice(
          Math.max(prevHistory.undo.length - maxHistoryLength, 0),
        );
        return {
          ...prevHistory,
          undo: [...slicedUndoHistory, asHistoryState(updatedIdea)],
        };
      });
    },
    [asHistoryState],
  );

  const undoChanges = useCallback(() => {
    if (historyState.undo.length === 0 || !idea) {
      return;
    }
    const undoItem = historyState.undo[historyState.undo.length - 1];
    if (!undoItem) {
      return;
    }
    const partial = diffIdeaUpdatePayload(idea, undoItem);
    api.setQueryData(api.ideas.get, [ideaId], (prev) => {
      if (!prev) {
        return prev;
      }
      return Object.assign(
        {},
        prev,
        undoItem.templated !== undefined
          ? { templated: undoItem.templated }
          : {},
        undoItem.name !== undefined ? { name: undoItem.name } : {},
        undoItem.coverImage !== undefined
          ? { coverImage: undoItem.coverImage }
          : {},
      );
    });
    if (Object.keys(partial).length > 0) {
      saveIdeaPartial(partial);
    }
    setHistoryState((prevHistory) => ({
      redo: [...prevHistory.redo, asHistoryState(idea)],
      undo: prevHistory.undo.slice(0, -1),
    }));
  }, [historyState.undo, idea, ideaId, saveIdeaPartial, asHistoryState]);

  const redoChanges = useCallback(() => {
    if (historyState.redo.length === 0 || !idea) {
      return;
    }
    const redoItem = historyState.redo[historyState.redo.length - 1];
    if (!redoItem) {
      return;
    }
    const partial = diffIdeaUpdatePayload(idea, redoItem);
    api.setQueryData(api.ideas.get, [ideaId], (prev) => {
      if (!prev) {
        return prev;
      }
      return Object.assign(
        {},
        prev,
        redoItem.templated !== undefined
          ? { templated: redoItem.templated }
          : {},
        redoItem.name !== undefined ? { name: redoItem.name } : {},
        redoItem.coverImage !== undefined
          ? { coverImage: redoItem.coverImage }
          : {},
      );
    });
    if (Object.keys(partial).length > 0) {
      saveIdeaPartial(partial);
    }
    setHistoryState((prevHistory) => ({
      redo: prevHistory.redo.slice(0, -1),
      undo: [...prevHistory.undo, asHistoryState(idea)],
    }));
  }, [historyState.redo, idea, ideaId, saveIdeaPartial, asHistoryState]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === 90 && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) {
          redoChanges();
        } else {
          undoChanges();
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [redoChanges, undoChanges]);

  /**
   * `update` can either be:
   *   - data which will be inserted into the idea at the path
   *   - a function which will be called with the current idea and
   *     should only return the data to be inserted at the path
   */
  const updateIdea: UpdateIdeaFn = useCallback(
    (update: unknown, path: string[], suppressSave = false) => {
      if (update === true && path.includes("isSubmitted")) {
        websocketSubscription.current?.publish("ideaSubmitted");
      }

      let prevIdea: OpenAPI.Schemas["Idea"] | null = null;
      let updatedIdea: OpenAPI.Schemas["Idea"] | null = null;
      api.setQueryData(api.ideas.get, [ideaId], (prev) => {
        if (!prev) {
          return prev;
        }
        prevIdea = prev;

        if (!ideaAtStartOfUpdate.current) {
          ideaAtStartOfUpdate.current = prevIdea;
        }
        const pathUpdate = reducePath(
          prev,
          update as Partial<IdeaDetail>,
          path,
        );
        const nextIdea = Object.assign(
          {},
          prev,
          typeof pathUpdate === "object" && pathUpdate !== null
            ? pathUpdate
            : {},
        ) as IdeaDetail;
        updatedIdea = nextIdea;

        return nextIdea;
      });

      if (suppressSave) {
        if (updatedIdea) {
          util.onUpdateIdea(updatedIdea, update, path);
        }
        return;
      }

      pendingChangesRef.current = mergeIdeaUpdatePayload(
        pendingChangesRef.current,
        update,
        path,
      );
      updatedAt.current = new Date();
      setUnsaved(true);

      if (editTimeout.current !== null) {
        clearTimeout(editTimeout.current);
        editTimeout.current = null;
      }

      editTimeout.current = setTimeout(() => {
        if (!updatedIdea) {
          return;
        }
        flushPendingChanges();
        util.onUpdateIdea(updatedIdea, update, path);
        websocketSubscription.current?.publish("fieldEdited", {
          fieldId: path,
          fieldData: update,
        });
        setHistoryState((prevHistory) => ({ ...prevHistory, redo: [] }));
        if (ideaAtStartOfUpdate.current) {
          addHistoryEntry(asHistoryState(ideaAtStartOfUpdate.current));
        }

        ideaAtStartOfUpdate.current = null;
        editTimeout.current = null;
      }, 400);
    },
    [ideaId, asHistoryState, addHistoryEntry, flushPendingChanges],
  );

  const handleWebsocket = useCallback(
    (data: IdeaWebsocketEvent) => {
      const { channel, fieldData, toUser } = data;
      // Discard any messages that were sent by the current user
      if (
        (toUser && toUser !== userId) ||
        (data.user && data.user._id === userId)
      ) {
        return;
      }

      switch (channel) {
        // Make sure that if a user opens an idea they get the proper response back if someone else was already in it to begin with
        case "userSubscribed":
          websocketSubscription.current?.publish("userWasSubscribed", {
            toUser: data.user._id,
          });
          setWebsocketState((prevState) => ({
            ...prevState,
            otherUsers: { ...prevState.otherUsers, [data.user._id]: data.user },
          }));
          break;
        case "userWasSubscribed":
          setWebsocketState((prevState) => ({
            ...prevState,
            otherUsers: { ...prevState.otherUsers, [data.user._id]: data.user },
          }));
          break;
        case "userUnsubscribed":
          setWebsocketState((prevState) => {
            const { [data.user._id]: _delUser, ...keepUsers } =
              prevState.otherUsers;
            return {
              ...prevState,
              otherUsers: keepUsers,
              externalChanges: removeUserFromExternalChanges(
                prevState.externalChanges,
                data.user._id,
              ),
            };
          });
          break;
        case "ideaSubmitted":
          reloadIdea();
          toast(
            `${data.user.profile?.fullName || data.user.name || "A collaborator"} submitted the ${t("generic.idea")}.`,
          );
          break;
        case "fieldEdited": {
          const editPath = resolveEditPath(data.fieldId, data.templated);
          if (!editPath.length) break;

          const presencePath = presencePathFor(editPath);
          setWebsocketState((prevState) => {
            // Only the most recently edited field should show this user
            const cleared = removeUserFromExternalChanges(
              prevState.externalChanges,
              data.user._id,
            );
            const externalChanges = { ...cleared } as Record<string, unknown>;
            let change: Record<string, unknown> = externalChanges;

            presencePath.forEach((field, i) => {
              if (i < presencePath.length - 1) {
                const nested = {
                  ...((change[field] as Record<string, unknown>) || {}),
                };
                change[field] = nested;
                change = nested;
                return;
              }

              const editors = normalizeEditors(change[field]);
              editors[data.user._id] = {
                ...data.user,
                isEditing: { fieldId: editPath, fieldData },
              };
              change[field] = editors;
            });

            return {
              ...prevState,
              otherUsers: {
                ...prevState.otherUsers,
                [data.user._id]: {
                  ...prevState.otherUsers[data.user._id],
                  ...data.user,
                },
              },
              externalChanges: externalChanges as IdeaExternalChanges,
            };
          });
          updateIdea(fieldData, editPath, true);
          break;
        }
        case "__websocketError":
          setWebsocketState((prevState) => ({
            ...prevState,
            websocketConnected: false,
          }));
          break;
        default:
          break;
      }
    },
    [userId, updateIdea, reloadIdea, t],
  );

  websocketSubscriptionHandler.current = handleWebsocket;

  // Keep a stable subscription; only recreate when idea/collaborator quiet-mode changes.
  // Handler updates go through the ref so we don't miss userWasSubscribed during resubscribes.
  useEffect(() => {
    if (!ideaId || !ideaReady) {
      if (websocketSubscription.current) {
        websocketSubscription.current.unsubscribe();
        websocketSubscription.current = null;
      }
      return;
    }

    const topic = `idea-${ideaId}`;
    const quiet = !isCollaborator;
    if (websocketSubscription.current) {
      if (
        websocketSubscription.current.topic === topic &&
        websocketSubscription.current.options.quiet === quiet
      ) {
        return;
      }
      websocketSubscription.current.unsubscribe();
      websocketSubscription.current = null;
    }

    websocketSubscription.current = websocketApi.subscribe(
      topic,
      (data) => websocketSubscriptionHandler.current?.(data),
      { receiveUserUpdates: true, receiveErrorUpdates: true, quiet },
    );
  }, [ideaId, ideaReady, isCollaborator]);

  useEffect(() => {
    return () => {
      if (websocketSubscription.current) {
        websocketSubscription.current.unsubscribe();
        websocketSubscription.current = null;
      }
    };
  }, []);

  return {
    ideaState,
    updateIdea,
    reloadIdea,
    undoChanges,
    redoChanges,
    canUndo: historyState.undo.length > 0,
    canRedo: historyState.redo.length > 0,
    otherUsers: websocketState.otherUsers,
    websocketConnected: websocketState.websocketConnected,
    externalChanges: websocketState.externalChanges,
  };
};
